import { apiUrl } from "../config/api";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

const API_USER_DETAILS = apiUrl('Auth/GetAuthUserDetails');
const STORAGE_KEY = "auth_token";

// Interface for feature
export interface Feature {
  fT_Id: number;
  fT_Name: string;
  url?: string;
  dashboardResponse: any;
}

// Interface for organization details
export interface OrganizationDetail {
  company_Code: string;
  organization_Name: string;
}

// Interface for user application
export interface UserApplication {
  aP_Id: number;
  aP_Name: string;
  url: string;
  features: Feature[];
  organizationDetails: OrganizationDetail[];
}

// Interface for API response data
export interface ApiResponseData {
  token: string;
  message: string;
  userDetails: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    hrmS_Code: string;
    failAttemptCount: number;
    isLocked: boolean;
    isActive: boolean;
    isLoginSucceeded: boolean;
    status: boolean;
    message: string;
    userRole: string;
    designation: string;
    sapCode: string;
    cu_Organization_Sapcode: string;
    cu_Organization_AccountCode?: string;
    isEmployee: boolean;
    isVendor?: boolean;
    isCustomer?: boolean;
    isSalesPerson?: boolean;
    isDesigner?: boolean;
    needToPasswordReset: boolean;
  };
  status: boolean;
  userApplications: UserApplication[];
  organizationList: OrganizationDetail[];
}

// Interface for GetAuthUserDetails API response
export interface AuthUserDetails {
  userId: number;
  email: string;
  userName: string;
  role: string;
  hrmsCode: string;
  password: string | null;
  designation: string;
  isEmployee: boolean;
  isAuthenticated: boolean;
  expTime: string;
  apiResponse: {
    statusCode: number;
    message: string;
    responseData: ApiResponseData;
  };
}

/* storage helpers */
export function setToken(token: string): void {
  try {
    // Save token under canonical key and common alternates for compatibility
    localStorage.setItem(STORAGE_KEY, token);
    try {
      localStorage.setItem('Bearer', token);
      localStorage.setItem('access_token', token);
    } catch {}
    console.debug("[auth] token saved (keys: auth_token, Bearer, access_token)");
  } catch (err) {
    console.warn("[auth] setToken failed", err);
  }
}

export function getToken(): string | null {
  try {
    // Check canonical key first, then common alternates
    const a = localStorage.getItem(STORAGE_KEY);
    if (a) return a;
    const b = localStorage.getItem('Bearer');
    if (b) return b;
    const c = localStorage.getItem('access_token');
    if (c) return c;
    const d = localStorage.getItem('authToken');
    if (d) return d;
    return null;
  } catch (err) {
    console.warn("[auth] getToken failed", err);
    return null;
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.debug("[auth] token cleared");
  } catch (err) {
    console.warn("[auth] clearToken failed", err);
  }
}

/* extract token from URL and clean it */
export function extractTokenFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) return null;

    params.delete("token");
    const newSearch = params.toString();
    const newUrl =
      window.location.origin +
      window.location.pathname +
      (newSearch ? `?${newSearch}` : "");
    window.history.replaceState({}, "", newUrl);

    console.debug("[auth] extracted token from URL");
    return token;
  } catch (err) {
    console.warn("[auth] extractTokenFromUrl error:", err);
    return null;
  }
}

/* Disable dev bypass entirely (always false) */
function isDevBypassEnabled(): boolean {
  // If you want to enable bypass during dev, uncomment this line and set VITE_DEV_BYPASS_AUTH=true in .env
  // return import.meta.env?.VITE_DEV_BYPASS_AUTH === "true";
  return false;
}

/**
 * initAuthFromUrl
 * - extracts token from URL and saves it
 * - returns current token or null
 */
export async function initAuthFromUrl(): Promise<string | null> {
  if (isDevBypassEnabled()) {
    console.info("[auth] DEV_BYPASS enabled (should be disabled in prod!)");
    return getToken();
  }

  const tokenFromUrl = extractTokenFromUrl();
  if (tokenFromUrl) {
    setToken(tokenFromUrl);
    return tokenFromUrl;
  }

  return getToken();
}

/**
 * fetchAuthUser - validates token via API
 */
export async function fetchAuthUser(): Promise<AuthUserDetails> {
  const token = getToken();
  if (!token) throw new Error("No auth token available");

  // Simple in-memory cache to avoid calling the auth endpoint repeatedly
  // during a single SPA session. Cache is keyed by token value.
  type CacheEntry = {
    token: string;
    promise: Promise<AuthUserDetails> | null;
    data: AuthUserDetails | null;
  };
  (fetchAuthUser as any)._cache = (fetchAuthUser as any)._cache || {
    entry: null as CacheEntry | null,
  };
  const cache: { entry: CacheEntry | null } = (fetchAuthUser as any)._cache;

  // If a cached entry exists for the same token and data present, return it.
  if (cache.entry && cache.entry.token === token && cache.entry.data) {
    return cache.entry.data;
  }

  // If there's an in-flight promise for the same token, return that promise.
  if (cache.entry && cache.entry.token === token && cache.entry.promise) {
    return cache.entry.promise;
  }

  console.debug("[auth] validating token with GetAuthUserDetails...");

  const promise = (async (): Promise<AuthUserDetails> => {
    let resp: Response;
    try {
      resp = await fetchWithTimeout(API_USER_DETAILS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err: any) {
      // Drop the cached in-flight promise so the next call actually retries
      // instead of forever returning this same rejection.
      if (cache.entry && cache.entry.token === token) cache.entry = null;
      const msg =
        err?.name === "AbortError"
          ? "GetAuthUserDetails timed out — the server took too long to respond."
          : `GetAuthUserDetails network error: ${err?.message ?? String(err)}`;
      console.warn("[auth]", msg);
      throw new Error(msg);
    }
    // mirror token under common key for other libs
    try { localStorage.setItem('Bearer', token); } catch {}

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      clearToken();
      if (cache.entry && cache.entry.token === token) cache.entry = null;
      const msg = `GetAuthUserDetails failed: ${resp.status} ${text}`;
      console.warn("[auth]", msg);
      throw new Error(msg);
    }

    const data = await resp.json().catch(() => null);
    console.debug("[auth] GetAuthUserDetails success", data);
    // store result in cache
    cache.entry = { token, promise: null, data };
    return data;
  })();

  // store in-flight promise
  cache.entry = { token, promise, data: null };
  return promise;
}
